import { useMemo, useState } from 'react';
import { radius } from '@karehero/llama/theme/radius';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  createTheme,
  ThemeProvider,
  useTheme,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles';

// A live design-system sandbox: theme controls on the left, a wall of MUI
// components on the right that re-render as you tweak. The point is to try DS
// changes here and see them instantly, then copy the generated override into
// the real theme — rather than editing llama and rebuilding to see each tweak.
// Tooling, so it deliberately reaches for raw MUI components (the theme styles
// them) rather than llama wrappers.

// A stock MUI theme — the sandbox baseline, deliberately free of any KareHero
// overrides so you're building up from vanilla MUI.
const MUI = createTheme();
const ROBOTO = '"Roboto", "Helvetica", "Arial", sans-serif';
const WORK_SANS = '"Work Sans", sans-serif';

const FONT_OPTIONS = [
  { label: 'Work Sans', value: WORK_SANS },
  { label: 'Roboto (MUI default)', value: ROBOTO },
  { label: 'Lora', value: 'Lora, serif' },
  { label: 'System', value: 'system-ui, sans-serif' },
];

type Preset = 'mui' | 'mobile';

type Knobs = {
  preset: Preset;
  primary: string;
  secondary: string;
  background: string;
  paper: string;
  radius: number;
  headingFont: string;
  bodyFont: string;
  headingItalic: boolean;
};

// A mobile-first type scale. Body and labels never drop below 16px (1rem);
// caption/overline are the only sub-16 tokens (14px), for non-essential microcopy
// only. `display` is a separate big italic style, mapped to a non-heading element.
const MOBILE_TYPOGRAPHY: Record<string, Record<string, string | number>> = {
  display: {
    fontFamily: 'Lora, serif',
    fontStyle: 'italic',
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h1: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3 },
  h2: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },
  h3: { fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.35 },
  h4: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.4 },
  h5: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.4 },
  h6: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.4 },
  subtitle1: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
  subtitle2: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
  body1: { fontSize: '1.0625rem', fontWeight: 400, lineHeight: 1.5 },
  body2: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
  button: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.25,
    textTransform: 'none',
  },
  caption: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.4 },
  overline: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.4,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
};

const HEADING_KEYS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

// The typography the sandbox applies, given the knobs — used to build the
// preview theme and to serialise the copyable snippet, so they never diverge.
const effectiveTypography = (k: Knobs): Record<string, unknown> => {
  const heading = {
    fontFamily: k.headingFont,
    fontStyle: k.headingItalic ? 'italic' : 'normal',
  };
  const typo: Record<string, unknown> = { fontFamily: k.bodyFont };
  if (k.preset === 'mobile') {
    for (const [key, value] of Object.entries(MOBILE_TYPOGRAPHY))
      typo[key] = value;
    // Headings keep the mobile sizes, but take family/italic from the knobs.
    for (const h of HEADING_KEYS)
      typo[h] = { ...MOBILE_TYPOGRAPHY[h], ...heading };
  } else {
    for (const h of HEADING_KEYS) typo[h] = heading;
  }
  return typo;
};

// The proposed brand display style — big italic Lora, decoupled from any
// heading level so it never lands as an <h1> in the accessibility tree.
const DISPLAY_SX = {
  fontFamily: 'Lora, serif',
  fontStyle: 'italic' as const,
  fontWeight: 600,
  fontSize: '1.75rem',
  lineHeight: 1.2,
};

// A minimal colour field — native picker + hex, fine for a dev tool.
const ColourField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
    <Box
      component='input'
      type='color'
      value={value}
      onChange={(e) => onChange((e.target as HTMLInputElement).value)}
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        p: 0,
        border: 1,
        borderColor: 'custom.neutral.200',
        borderRadius: '8px',
        cursor: 'pointer',
      }}
      aria-label={label}
    />
    <TextField
      fullWidth
      size='small'
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </Stack>
);

// The generated theme, ready to paste. Starts from stock MUI (no KareHero base)
// — the sandbox baseline — so this is vanilla MUI plus your tweaks / the mobile
// scale. Serialised from the same builder that drives the preview.
const overridesSnippet = (k: Knobs): string => {
  const typo = effectiveTypography(k);
  const body = Object.entries(typo)
    .filter(([key]) => key !== 'fontFamily')
    .map(([key, value]) => `    ${key}: ${JSON.stringify(value)},`)
    .join('\n');
  return `createTheme({
  palette: {
    primary: { main: '${k.primary}' },
    secondary: { main: '${k.secondary}' },
    background: { default: '${k.background}', paper: '${k.paper}' },
  },
  shape: { borderRadius: ${k.radius} },
  typography: {
    fontFamily: '${k.bodyFont}',
${body}
  },
  components: {
    MuiTypography: { defaultProps: { variantMapping: { display: 'p' } } },
  },
})`;
};

const Controls = ({
  knobs,
  set,
}: {
  knobs: Knobs;
  set: (patch: Partial<Knobs>) => void;
}) => (
  <Stack spacing={2.5}>
    <Box>
      <Typography variant='overline' color='text.secondary'>
        Baseline
      </Typography>
      <ToggleButtonGroup
        className='segmented'
        exclusive
        size='small'
        fullWidth
        value={knobs.preset}
        onChange={(_, v: Preset | null) => v && set({ preset: v })}
        sx={{ mt: 0.5 }}
      >
        <ToggleButton value='mui'>Stock MUI</ToggleButton>
        <ToggleButton value='mobile'>Mobile</ToggleButton>
      </ToggleButtonGroup>
    </Box>

    <Divider />
    <Typography variant='overline' color='text.secondary'>
      Palette
    </Typography>
    <ColourField
      label='Primary'
      value={knobs.primary}
      onChange={(v) => set({ primary: v })}
    />
    <ColourField
      label='Secondary'
      value={knobs.secondary}
      onChange={(v) => set({ secondary: v })}
    />
    <ColourField
      label='Background'
      value={knobs.background}
      onChange={(v) => set({ background: v })}
    />
    <ColourField
      label='Surface'
      value={knobs.paper}
      onChange={(v) => set({ paper: v })}
    />

    <Divider />
    <Typography variant='overline' color='text.secondary'>
      Shape & type
    </Typography>
    <Box>
      <Typography variant='body2' color='text.secondary' gutterBottom>
        Corner radius — {knobs.radius}px
      </Typography>
      <Slider
        value={knobs.radius}
        min={0}
        max={28}
        onChange={(_, v) => set({ radius: v as number })}
      />
    </Box>
    <TextField
      select
      fullWidth
      size='small'
      label='Heading font'
      value={knobs.headingFont}
      onChange={(e) => set({ headingFont: e.target.value })}
    >
      {FONT_OPTIONS.map((f) => (
        <MenuItem key={f.value} value={f.value}>
          {f.label}
        </MenuItem>
      ))}
    </TextField>
    <TextField
      select
      fullWidth
      size='small'
      label='Body font'
      value={knobs.bodyFont}
      onChange={(e) => set({ bodyFont: e.target.value })}
    >
      {FONT_OPTIONS.map((f) => (
        <MenuItem key={f.value} value={f.value}>
          {f.label}
        </MenuItem>
      ))}
    </TextField>
    <FormControlLabel
      control={
        <Switch
          checked={knobs.headingItalic}
          onChange={(e) => set({ headingItalic: e.target.checked })}
        />
      }
      label='Italic headings'
    />
  </Stack>
);

// Every typography variant on a theme, in a sensible order, with custom ones
// appended — read live so the scale can't fall out of sync.
const VARIANT_ORDER = [
  'display1',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'button',
  'caption',
  'overline',
];

const typographyVariants = (theme: Theme): string[] => {
  const typo = theme.typography as unknown as Record<string, unknown>;
  const names = Object.entries(typo)
    .filter(
      ([key, value]) =>
        key !== 'allVariants' &&
        value &&
        typeof value === 'object' &&
        'fontSize' in (value as Record<string, unknown>),
    )
    .map(([key]) => key);
  return names.sort((a, b) => {
    const ia = VARIANT_ORDER.indexOf(a);
    const ib = VARIANT_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
};

// A real sentence, so each variant shows its size in context rather than just
// its name.
const SPECIMEN = 'Let’s sort out Mum’s care.';

// The full type scale for a theme: the specimen sentence rendered in each
// variant's style, with the token name and spec on the line beneath.
const TypeSpecimens = ({ theme }: { theme: Theme }) => {
  const typo = theme.typography as unknown as Record<
    string,
    {
      fontSize?: string | number;
      fontWeight?: string | number;
      fontFamily?: string;
    }
  >;
  return (
    <Stack spacing={2}>
      {typographyVariants(theme).map((name) => {
        const t = typo[name] ?? {};
        const family = String(t.fontFamily ?? '')
          .split(',')[0]
          .replace(/["']/g, '');
        return (
          <Box key={name}>
            <Typography
              variant={name as never}
              sx={{ display: 'block', mb: 0.25 }}
            >
              {SPECIMEN}
            </Typography>
            <Typography variant='mono' color='text.secondary'>
              {[name, t.fontSize, t.fontWeight, family]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

// A labelled block in the component wall.
const Sample = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box>
    <Typography variant='overline' color='text.secondary'>
      {title}
    </Typography>
    <Box sx={{ mt: 1 }}>{children}</Box>
  </Box>
);

const ComponentWall = () => {
  const [tab, setTab] = useState(0);
  // The wall renders under the preview theme, so this is that theme's scale.
  const wallTheme = useTheme();
  return (
    <Stack spacing={3}>
      <Sample title='Type scale'>
        <TypeSpecimens theme={wallTheme} />
      </Sample>

      <Sample title='Headings vs display (accessibility)'>
        <Stack spacing={2}>
          <Typography variant='body2' color='text.secondary'>
            A big brand line should be a <em>display</em> style, not an{' '}
            <code>h1</code> — the visual size is decoupled from the semantics,
            so it doesn’t hijack the heading outline screen readers rely on.
          </Typography>

          <Box>
            <Typography component='p' sx={DISPLAY_SX}>
              Let’s sort out Mum’s care.
            </Typography>
            <Typography variant='mono' color='text.secondary'>
              display · renders as &lt;p&gt; · not in the heading outline
            </Typography>
          </Box>

          <Box>
            <Typography variant='h4' component='h1'>
              Your claims
            </Typography>
            <Typography variant='mono' color='text.secondary'>
              variant=&apos;h4&apos; · renders as &lt;h1&gt; · the real page
              heading
            </Typography>
          </Box>

          <Box
            component='pre'
            sx={{
              p: 2,
              borderRadius: `${radius.md}px`,
              bgcolor: 'custom.neutral.100',
              overflow: 'auto',
              fontSize: 12,
              fontFamily: 'monospace',
              whiteSpace: 'pre',
            }}
          >
            {`// A display style, decoupled from heading semantics
<Typography variant='display1' component='p'>…</Typography>

// theme
typography: { display1: { fontFamily: 'Lora', fontStyle: 'italic', … } }
components: {
  MuiTypography: { defaultProps: { variantMapping: { display1: 'p' } } },
}`}
          </Box>
        </Stack>
      </Sample>

      <Sample title='Pairings'>
        <Stack spacing={2}>
          {/* page header */}
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='h1' gutterBottom>
                Mum’s care
              </Typography>
              <Typography variant='body1' color='text.secondary'>
                Everything in one place. Here’s where things are up to and what
                needs you next.
              </Typography>
            </CardContent>
          </Card>

          {/* eyebrow + section heading + body */}
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='overline' color='primary'>
                This week
              </Typography>
              <Typography variant='h2' gutterBottom>
                3 things to do
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                A couple are quick. Keni’s filled in the rest from what you told
                her.
              </Typography>
            </CardContent>
          </Card>

          {/* list row: title + secondary */}
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='subtitle1'>Attendance Allowance</Typography>
              <Typography variant='body2' color='text.secondary'>
                Ready to submit · up to £5,959 a year
              </Typography>
            </CardContent>
          </Card>

          {/* card: heading + body + meta */}
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='h3' gutterBottom>
                Photo Mum’s prescription
              </Typography>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                One photo gets her NHS number and full medicine list — no
                typing.
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                For Mum’s Attendance Allowance
              </Typography>
            </CardContent>
          </Card>

          {/* form field: label + input + helper */}
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='subtitle2' gutterBottom>
                National Insurance number
              </Typography>
              <TextField fullWidth size='small' placeholder='QQ 12 34 56 C' />
              <Typography
                sx={{ mt: 0.5, display: 'block' }}
                variant='caption'
                color='text.secondary'
              >
                You’ll find it on a payslip or letter from HMRC.
              </Typography>
            </CardContent>
          </Card>

          {/* stat: display figure + label */}
          <Card variant='outlined'>
            <CardContent>
              <Typography component='p' sx={DISPLAY_SX} color='primary'>
                £11,344
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Estimated a year
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Sample>

      <Sample title='Buttons'>
        <Stack sx={{ flexWrap: 'wrap' }} direction='row' spacing={2} useFlexGap>
          <Button variant='contained'>Contained</Button>
          <Button variant='outlined'>Outlined</Button>
          <Button variant='text'>Text</Button>
          <Button variant='contained' color='secondary'>
            Secondary
          </Button>
          <Button variant='contained' disabled>
            Disabled
          </Button>
          <Button variant='contained' size='small'>
            Small
          </Button>
        </Stack>
      </Sample>

      <Sample title='Form controls'>
        <Stack sx={{ maxWidth: 360 }} spacing={2}>
          <TextField label='Text field' placeholder='Type here…' fullWidth />
          <TextField select label='Select' defaultValue='one' fullWidth>
            <MenuItem value='one'>Option one</MenuItem>
            <MenuItem value='two'>Option two</MenuItem>
          </TextField>
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
            <FormControlLabel
              control={<Checkbox defaultChecked />}
              label='Checkbox'
            />
            <FormControlLabel
              control={<Switch defaultChecked />}
              label='Switch'
            />
          </Stack>
          <RadioGroup row defaultValue='a'>
            <FormControlLabel value='a' control={<Radio />} label='One' />
            <FormControlLabel value='b' control={<Radio />} label='Two' />
          </RadioGroup>
          <Slider defaultValue={40} />
        </Stack>
      </Sample>

      <Sample title='Chips'>
        <Stack sx={{ flexWrap: 'wrap' }} direction='row' spacing={1} useFlexGap>
          <Chip label='Default' />
          <Chip label='Primary' color='primary' />
          <Chip label='Outlined' variant='outlined' />
          <Chip label='Deletable' onDelete={() => undefined} />
        </Stack>
      </Sample>

      <Sample title='Surfaces'>
        <Stack sx={{ flexWrap: 'wrap' }} direction='row' spacing={2} useFlexGap>
          <Card sx={{ width: 220 }}>
            <CardContent>
              <Typography variant='subtitle1'>Card</Typography>
              <Typography variant='body2' color='text.secondary'>
                A surface with the current radius and paper colour.
              </Typography>
            </CardContent>
          </Card>
          <Paper sx={{ width: 220, p: 2 }}>
            <Typography variant='subtitle1'>Paper</Typography>
            <Typography variant='body2' color='text.secondary'>
              The base surface primitive.
            </Typography>
          </Paper>
        </Stack>
      </Sample>

      <Sample title='Feedback'>
        <Stack spacing={1.5}>
          <Alert severity='success'>Success message</Alert>
          <Alert severity='info'>Info message</Alert>
          <Alert severity='warning'>Warning message</Alert>
          <Alert severity='error'>Error message</Alert>
          <LinearProgress />
        </Stack>
      </Sample>

      <Sample title='Tabs'>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label='First' />
          <Tab label='Second' />
          <Tab label='Third' />
        </Tabs>
      </Sample>
    </Stack>
  );
};

// ── Colour token reference ─────────────────────────────────────────────────
// Reads a theme and shows how its colour tokens are wired: the semantic roles
// (primary, error, …) and the ramps (MUI's grey, plus any brand ramps under
// palette.custom.*) — each with the exact token path to reference it.

const isHex = (v: unknown): v is string =>
  typeof v === 'string' && v.startsWith('#');

const Swatch = ({ path, value }: { path: string; value: string }) => (
  <Box sx={{ width: 92 }}>
    <Box
      sx={{
        height: 44,
        borderRadius: '8px',
        border: 1,
        borderColor: 'custom.neutral.200',
        backgroundColor: value,
      }}
    />
    <Typography variant='mono' sx={{ display: 'block', mt: 0.5 }}>
      {path}
    </Typography>
    <Typography variant='mono' color='text.secondary'>
      {value}
    </Typography>
  </Box>
);

const TokenRamp = ({
  label,
  swatches,
}: {
  label: string;
  swatches: { path: string; value: string }[];
}) => {
  if (swatches.length === 0) return null;
  return (
    <Box>
      <Typography variant='subtitle2' gutterBottom>
        {label}
      </Typography>
      <Stack sx={{ flexWrap: 'wrap' }} direction='row' spacing={1.5} useFlexGap>
        {swatches.map((s) => (
          <Swatch key={s.path} path={s.path} value={s.value} />
        ))}
      </Stack>
    </Box>
  );
};

const SEMANTIC_ROLES = [
  'primary',
  'secondary',
  'error',
  'warning',
  'info',
  'success',
] as const;

const ColourTokens = ({ theme }: { theme: Theme }) => {
  const palette = theme.palette as unknown as Record<string, unknown>;

  // Semantic roles → main / light / dark.
  const semantic = SEMANTIC_ROLES.map((role) => {
    const entry = palette[role] as Record<string, unknown> | undefined;
    const swatches = ['main', 'light', 'dark']
      .filter((k) => entry && isHex(entry[k]))
      .map((k) => ({
        path: `${role}.${k}`,
        value: (entry as Record<string, string>)[k],
      }));
    return { role, swatches };
  }).filter((r) => r.swatches.length > 0);

  // Ramps: MUI's grey, plus any brand ramps under palette.custom.*
  const ramps: {
    label: string;
    swatches: { path: string; value: string }[];
  }[] = [];
  const pushRamp = (
    label: string,
    obj: Record<string, unknown>,
    prefix: string,
  ) => {
    const swatches = Object.entries(obj)
      .filter(([, v]) => isHex(v))
      .map(([shade, v]) => ({ path: `${prefix}${shade}`, value: v as string }));
    if (swatches.length) ramps.push({ label, swatches });
  };
  if (palette.grey && typeof palette.grey === 'object')
    pushRamp('grey', palette.grey as Record<string, unknown>, 'grey.');
  const custom = palette.custom;
  if (custom && typeof custom === 'object') {
    for (const [key, val] of Object.entries(
      custom as Record<string, unknown>,
    )) {
      if (isHex(val))
        ramps.push({
          label: key,
          swatches: [{ path: `custom.${key}`, value: val }],
        });
      else if (val && typeof val === 'object')
        pushRamp(key, val as Record<string, unknown>, `custom.${key}.`);
    }
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant='overline' color='text.secondary'>
          Semantic roles
        </Typography>
        <Stack sx={{ mt: 1 }} spacing={2}>
          {semantic.map((r) => (
            <TokenRamp key={r.role} label={r.role} swatches={r.swatches} />
          ))}
        </Stack>
      </Box>

      {ramps.length > 0 && (
        <Box>
          <Typography variant='overline' color='text.secondary'>
            Ramps
          </Typography>
          <Stack sx={{ mt: 1 }} spacing={2}>
            {ramps.map((r) => (
              <TokenRamp key={r.label} label={r.label} swatches={r.swatches} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

const Playground = () => {
  // Sandbox defaults to the proposed mobile scale; flip to Stock MUI to compare.
  const [knobs, setKnobs] = useState<Knobs>(() => ({
    preset: 'mobile',
    primary: MUI.palette.primary.main,
    secondary: MUI.palette.secondary.main,
    background: MUI.palette.background.default,
    paper: MUI.palette.background.paper,
    radius:
      typeof MUI.shape.borderRadius === 'number' ? MUI.shape.borderRadius : 4,
    headingFont: WORK_SANS,
    bodyFont: WORK_SANS,
    headingItalic: false,
  }));

  const set = (patch: Partial<Knobs>) =>
    setKnobs((prev) => ({ ...prev, ...patch }));

  // Built from stock MUI (no KareHero base) so the wall is vanilla MUI + knobs.
  const previewTheme = useMemo<Theme>(
    () =>
      createTheme({
        palette: {
          primary: { main: knobs.primary },
          secondary: { main: knobs.secondary },
          background: { default: knobs.background, paper: knobs.paper },
        },
        shape: { borderRadius: knobs.radius },
        typography: effectiveTypography(knobs) as ThemeOptions['typography'],
      }),
    [knobs],
  );

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant='h3'>Design system sandbox</Typography>
        <Typography variant='body2' color='text.secondary'>
          Pick a baseline (Stock MUI or the proposed Mobile scale), tweak on the
          left, watch it re-render, then copy the theme into the real DS. The
          MUI reference underneath shows the untuned defaults for comparison.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
          gap: 4,
          alignItems: 'start',
        }}
      >
        {/* controls + generated override (sticky on desktop) */}
        <Stack sx={{ position: { md: 'sticky' }, top: { md: 24 } }} spacing={3}>
          <Paper variant='outlined' sx={{ p: 2.5 }}>
            <Controls knobs={knobs} set={set} />
          </Paper>
          <Box>
            <Typography variant='overline' color='text.secondary'>
              Theme override
            </Typography>
            <Box
              component='pre'
              sx={{
                mt: 1,
                p: 2,
                borderRadius: `${radius.md}px`,
                bgcolor: 'custom.neutral.100',
                overflow: 'auto',
                fontSize: 12,
                fontFamily: 'monospace',
                whiteSpace: 'pre',
              }}
            >
              {overridesSnippet(knobs)}
            </Box>
          </Box>
        </Stack>

        {/* live component wall under the previewed theme (stock MUI + knobs) */}
        <ThemeProvider theme={previewTheme}>
          <Paper
            variant='outlined'
            sx={{ p: 3, bgcolor: 'background.default' }}
          >
            <ComponentWall />
          </Paper>
        </ThemeProvider>
      </Box>

      {/* token reference — stock MUI theme, so you can see how MUI is wired */}
      <Divider sx={{ my: 4 }} />
      <Box>
        <Typography variant='h4'>How the MUI theme is wired</Typography>
        <Typography sx={{ mb: 2 }} variant='body2' color='text.secondary'>
          Read live from stock MUI — the semantic roles it ships (
          <code>primary</code>, <code>error</code>, …) and its <code>grey</code>{' '}
          ramp. Reference a token with the path shown, e.g.{' '}
          <code>color=&apos;primary.main&apos;</code>.
        </Typography>
        <ColourTokens theme={MUI} />
      </Box>

      <Divider sx={{ my: 4 }} />
      <Box>
        <Typography variant='h4'>The MUI type scale</Typography>
        <Typography sx={{ mb: 2 }} variant='body2' color='text.secondary'>
          MUI’s default variants and sizes — the web-first scale (h1 is 6rem /
          96px). Reference with <code>variant=&apos;h3&apos;</code>.
        </Typography>
        <TypeSpecimens theme={MUI} />
      </Box>
    </Box>
  );
};

Playground.displayName = 'Playground';
export default Playground;
